import { Box, Theme, Typography } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import updateLocale from 'dayjs/plugin/updateLocale.js'
import useStyles from '~/common/hooks/useStyles'
import { gray } from '~/common/styles/colors'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
import { ActivityEventItemType } from './ActivityTimeline'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '<1m',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mo',
    MM: '%dmo',
    y: '1y',
    yy: '%dy',
  },
})

function getStyles(theme: Theme) {
  return {
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      maxWidth: '100%',
      title: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        maxWidth: '80%',
        name: {
          color: theme.palette.text.primary,
          paddingInlineEnd: theme.spacing(0.5),
        },
        actionText: {
          color: theme.palette.text.secondary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 1,
        },
        workflowActionText: {
          color: theme.palette.text.primary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexShrink: 1,
        },
        time: {
          paddingInlineStart: theme.spacing(2),
          color: gray[10],
          '&:hover': {
            color: gray[11],
          },
          flex: '0 0 auto', // Don't allow the name to grow or shrink
        },
      },
    },
  }
}

interface ActivityItemHeaderProps {
  name: string
  action: string
  time?: string
  type: ActivityEventItemType
}

export function ActivityItemHeader({ name, action, time, type }: ActivityItemHeaderProps) {
  const styles = useStyles(getStyles)

  let actionLine = <></>
  switch (type) {
    case ActivityEventItemType.WORKFLOW:
    case ActivityEventItemType.INTEGRATION:
      actionLine = (
        <Typography sx={styles.header.title.workflowActionText} variant="subtitle1">
          {action}
        </Typography>
      )
      break
    case ActivityEventItemType.COMMENT:
    case ActivityEventItemType.CRUD:
      actionLine = (
        <>
          <Typography sx={styles.header.title.name} variant="subtitle1">
            {name}
          </Typography>
          <Typography sx={styles.header.title.actionText} variant="subtitle1">
            {action}
          </Typography>
        </>
      )
      break
  }

  return (
    <Box sx={styles.header}>
      <Box sx={styles.header.title}>{actionLine}</Box>
      {time && (
        <Tooltip arrow title={dayjs(time).format('ddd, MM/DD/YY [at] hh:mma')}>
          <Typography sx={styles.header.title.time} variant="subtitle2">
            {dayjs(time).fromNow()}
          </Typography>
        </Tooltip>
      )}
    </Box>
  )
}
